import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { SupabaseClient } from "@supabase/supabase-js";

import { fetchMembers, type MemberRow } from "../data/members";
import { fetchSites, type SiteRow } from "../data/routes";
import type { NavItem } from "./nav-model";

interface JumpPageDetails {
  description: string;
  keywords: readonly string[];
}

const PAGE_DETAILS: Readonly<Record<string, JumpPageDetails>> = {
  command: { description: "Live operations", keywords: ["home", "map", "cad"] },
  patrols: { description: "Route and checkpoint readiness", keywords: ["routes", "checkpoints"] },
  schedule: { description: "Templates, coverage, and open shifts", keywords: ["shifts", "coverage"] },
  setup: { description: "Sites, routes, and checkpoints", keywords: ["sites", "properties"] },
  feed: { description: "Account updates", keywords: ["posts", "updates"] },
  channels: { description: "Team conversations", keywords: ["messages", "threads"] },
  incidents: { description: "Incident records", keywords: ["alerts", "events"] },
  reports: { description: "Evidence and timeclock records", keywords: ["exports", "hours"] },
  admin: { description: "Members, devices, and policy", keywords: ["settings", "team"] },
  platform: { description: "Superadmin controls", keywords: ["accounts", "support"] },
  help: { description: "Console guides", keywords: ["articles", "documentation"] },
};

const FALLBACK_DETAILS: JumpPageDetails = {
  description: "Console page",
  keywords: [],
};

/** Cap object rows per kind so the list stays scannable. */
const OBJECT_RESULT_CAP = 8;

type JumpEntry =
  | {
      id: string;
      kind: "Page";
      pageKey: string;
      label: string;
      description: string;
      path: string;
    }
  | {
      id: string;
      kind: "Officer";
      label: string;
      description: string;
      path: "/admin";
    }
  | {
      id: string;
      kind: "Site";
      label: string;
      description: string;
      path: "/setup";
    };

function officerTitle(member: MemberRow): string {
  return (
    member.memberDisplayName?.trim() ||
    member.memberLabel?.trim() ||
    member.memberEmail?.trim() ||
    "Officer"
  );
}

function officerDescription(member: MemberRow): string {
  const title = officerTitle(member);
  const email = member.memberEmail?.trim();
  if (email && email.toLowerCase() !== title.toLowerCase()) return email;
  return member.role;
}

function memberMatchesQuery(member: MemberRow, normalizedQuery: string): boolean {
  const haystack = [member.memberDisplayName, member.memberLabel, member.memberEmail]
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .join(" ")
    .toLowerCase();
  return haystack.includes(normalizedQuery);
}

function pageMatchesQuery(item: NavItem, normalizedQuery: string): boolean {
  const details = PAGE_DETAILS[item.key] ?? FALLBACK_DETAILS;
  return [item.label, details.description, ...details.keywords]
    .join(" ")
    .toLowerCase()
    .includes(normalizedQuery);
}

function pageEntry(item: NavItem): JumpEntry {
  const details = PAGE_DETAILS[item.key] ?? FALLBACK_DETAILS;
  return {
    id: `jump-page-${item.key}`,
    kind: "Page",
    pageKey: item.key,
    label: item.label,
    description: details.description,
    path: item.path,
  };
}

function officerEntry(member: MemberRow): JumpEntry {
  return {
    id: `jump-officer-${member.membershipId}`,
    kind: "Officer",
    label: officerTitle(member),
    description: officerDescription(member),
    path: "/admin",
  };
}

function siteEntry(site: SiteRow): JumpEntry {
  return {
    id: `jump-site-${site.siteId}`,
    kind: "Site",
    label: site.siteName,
    description: "Open in Setup",
    path: "/setup",
  };
}

/**
 * Shell-level find-and-go palette. Pages come from the already permission-filtered nav model.
 * Officers and sites load from existing account-scoped read clients when the palette opens;
 * load failures omit that group instead of crashing.
 */
export function JumpPalette({
  items,
  client,
  accountId,
}: {
  items: readonly NavItem[];
  client: SupabaseClient;
  accountId: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const bodyOverflowRef = useRef("");
  const objectLoadGenRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentKeys, setRecentKeys] = useState<readonly string[]>([]);
  // undefined = omit group (idle, loading, or failed). Array = successful load for this account.
  const [officerRows, setOfficerRows] = useState<MemberRow[] | undefined>(undefined);
  const [siteRows, setSiteRows] = useState<SiteRow[] | undefined>(undefined);

  const itemByKey = useMemo(() => new Map(items.map((item) => [item.key, item])), [items]);
  const recentItems = useMemo(
    () => recentKeys.flatMap((key) => (itemByKey.has(key) ? [itemByKey.get(key)!] : [])),
    [itemByKey, recentKeys],
  );
  const recentKeySet = useMemo(() => new Set(recentItems.map((item) => item.key)), [recentItems]);
  const remainingItems = useMemo(
    () => items.filter((item) => !recentKeySet.has(item.key)),
    [items, recentKeySet],
  );

  const normalizedQuery = query.trim().toLowerCase();
  const hasQuery = normalizedQuery.length > 0;

  const filteredPages = useMemo(() => {
    if (!hasQuery) return items;
    return items.filter((item) => pageMatchesQuery(item, normalizedQuery));
  }, [hasQuery, items, normalizedQuery]);

  const filteredOfficers = useMemo(() => {
    if (!hasQuery || officerRows === undefined) return [];
    return officerRows
      .filter((member) => memberMatchesQuery(member, normalizedQuery))
      .slice(0, OBJECT_RESULT_CAP)
      .map(officerEntry);
  }, [hasQuery, normalizedQuery, officerRows]);

  const filteredSites = useMemo(() => {
    if (!hasQuery || siteRows === undefined) return [];
    return siteRows
      .filter((site) => site.siteName.toLowerCase().includes(normalizedQuery))
      .slice(0, OBJECT_RESULT_CAP)
      .map(siteEntry);
  }, [hasQuery, normalizedQuery, siteRows]);

  const navigationEntries = useMemo((): JumpEntry[] => {
    if (!hasQuery) {
      return [...recentItems.map(pageEntry), ...remainingItems.map(pageEntry)];
    }
    return [
      ...filteredPages.map(pageEntry),
      ...filteredOfficers,
      ...filteredSites,
    ];
  }, [filteredOfficers, filteredPages, filteredSites, hasQuery, recentItems, remainingItems]);

  const hasAnyResults = hasQuery
    ? filteredPages.length > 0 || filteredOfficers.length > 0 || filteredSites.length > 0
    : items.length > 0;

  const clearObjectResults = useCallback(() => {
    objectLoadGenRef.current += 1;
    setOfficerRows(undefined);
    setSiteRows(undefined);
  }, []);

  const loadObjectResults = useCallback(
    (forAccountId: string) => {
      const gen = ++objectLoadGenRef.current;
      setOfficerRows(undefined);
      setSiteRows(undefined);

      void fetchMembers(client, forAccountId)
        .then((rows) => {
          if (gen !== objectLoadGenRef.current) return;
          setOfficerRows(rows);
        })
        .catch(() => {
          if (gen !== objectLoadGenRef.current) return;
          setOfficerRows(undefined);
        });

      void fetchSites(client, forAccountId)
        .then((rows) => {
          if (gen !== objectLoadGenRef.current) return;
          setSiteRows(rows);
        })
        .catch(() => {
          if (gen !== objectLoadGenRef.current) return;
          setSiteRows(undefined);
        });
    },
    [client],
  );

  const rememberFocus = useCallback(() => {
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : triggerRef.current;
  }, []);

  const openPalette = useCallback(() => {
    rememberFocus();
    setOpen(true);
  }, [rememberFocus]);

  const closePalette = useCallback(() => {
    setOpen(false);
    setQuery("");
    setSelectedIndex(0);
    clearObjectResults();
  }, [clearObjectResults]);

  // Fetch workspace-scoped objects when the palette opens. Account changes while open clear
  // pending groups and re-fetch so no prior tenant rows linger.
  useEffect(() => {
    if (!open) return;
    loadObjectResults(accountId);
  }, [accountId, loadObjectResults, open]);

  useEffect(() => {
    function onShortcut(event: KeyboardEvent): void {
      if (
        event.repeat ||
        event.key.toLowerCase() !== "k" ||
        (!event.metaKey && !event.ctrlKey) ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      if (open) {
        closePalette();
      } else {
        openPalette();
      }
    }

    document.addEventListener("keydown", onShortcut);
    return () => document.removeEventListener("keydown", onShortcut);
  }, [closePalette, open, openPalette]);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!dialog.open) {
      bodyOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      dialog.showModal();
    }
    inputRef.current?.focus();

    return () => {
      document.body.style.overflow = bodyOverflowRef.current;
      finishClose();
    };
  }, [open]);

  useEffect(() => {
    closePalette();
  }, [closePalette, location.key]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query, accountId]);

  useEffect(() => {
    setSelectedIndex((current) =>
      navigationEntries.length === 0 ? 0 : Math.min(current, navigationEntries.length - 1),
    );
  }, [navigationEntries]);

  useEffect(() => {
    const selected = navigationEntries[selectedIndex];
    if (!open || !selected) return;
    document.getElementById(selected.id)?.scrollIntoView?.({ block: "nearest" });
  }, [navigationEntries, open, selectedIndex]);

  function finishClose(): void {
    document.body.style.overflow = bodyOverflowRef.current;
    const target = restoreFocusRef.current;
    if (target?.isConnected) {
      target.focus();
    } else {
      triggerRef.current?.focus();
    }
    restoreFocusRef.current = null;
  }

  function goTo(entry: JumpEntry): void {
    if (entry.kind === "Page") {
      setRecentKeys((current) =>
        [entry.pageKey, ...current.filter((key) => key !== entry.pageKey)].slice(0, 4),
      );
    }
    closePalette();
    navigate(entry.path);
  }

  function onDialogKeyDown(event: React.KeyboardEvent<HTMLDialogElement>): void {
    if (event.key === "Escape") {
      event.preventDefault();
      closePalette();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedIndex((current) =>
        navigationEntries.length === 0 ? 0 : (current + 1) % navigationEntries.length,
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedIndex((current) =>
        navigationEntries.length === 0
          ? 0
          : (current - 1 + navigationEntries.length) % navigationEntries.length,
      );
      return;
    }
    if (event.key === "Enter" && document.activeElement === dialogRef.current?.querySelector("input")) {
      const selected = navigationEntries[selectedIndex];
      if (selected) {
        event.preventDefault();
        goTo(selected);
      }
    }
  }

  const selectedEntry = navigationEntries[selectedIndex];
  const selectedItemId = selectedEntry?.id;
  const selectedPageKey = selectedEntry?.kind === "Page" ? selectedEntry.pageKey : undefined;

  return (
    <>
      <button
        ref={triggerRef}
        className="topbar__jump"
        type="button"
        aria-label="Open Jump palette"
        aria-keyshortcuts="Control+K Meta+K"
        onClick={openPalette}
      >
        <SearchIcon className="topbar__jump-icon" />
        <span className="topbar__jump-label">Jump</span>
        <kbd className="topbar__jump-shortcut">Cmd K</kbd>
      </button>

      {open ? createPortal(
        <dialog
          ref={dialogRef}
          className="jump-palette"
          aria-modal="true"
          aria-labelledby="jump-palette-title"
          aria-describedby="jump-palette-description"
          onCancel={(event) => {
            event.preventDefault();
            closePalette();
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) closePalette();
          }}
          onKeyDown={onDialogKeyDown}
        >
          <h2 id="jump-palette-title" className="jump-palette__sr-only">
            Jump to a console page
          </h2>
          <p id="jump-palette-description" className="jump-palette__sr-only">
            Search pages, officers, and sites you can access, then use the arrow keys and Enter to
            navigate.
          </p>

          <div className="jump-palette__search">
            <SearchIcon className="jump-palette__search-icon" />
            <input
              ref={inputRef}
              autoFocus
              aria-label="Jump to"
              aria-autocomplete="list"
              aria-controls="jump-palette-results"
              aria-expanded={open}
              aria-activedescendant={selectedItemId}
              role="combobox"
              autoComplete="off"
              placeholder="Jump to a page, officer, or site"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <kbd>Esc</kbd>
          </div>

          <div
            id="jump-palette-results"
            className="jump-palette__list"
            role="listbox"
            aria-label="Matching jump results"
          >
            {!hasAnyResults ? (
              <div className="jump-palette__empty" role="status">
                <strong>No matches</strong>
                <span>Try a page, officer, or site name.</span>
              </div>
            ) : !hasQuery ? (
              <>
                {recentItems.length > 0 ? (
                  <JumpPageGroup
                    heading="Recent"
                    items={recentItems}
                    selectedKey={selectedPageKey}
                    onSelect={(item) => goTo(pageEntry(item))}
                  />
                ) : null}
                <JumpPageGroup
                  heading="Pages"
                  items={remainingItems}
                  selectedKey={selectedPageKey}
                  onSelect={(item) => goTo(pageEntry(item))}
                />
              </>
            ) : (
              <>
                <JumpPageGroup
                  heading="Pages"
                  items={filteredPages}
                  selectedKey={selectedPageKey}
                  onSelect={(item) => goTo(pageEntry(item))}
                />
                <JumpObjectGroup
                  heading="Officers"
                  entries={filteredOfficers}
                  selectedId={selectedItemId}
                  onSelect={goTo}
                />
                <JumpObjectGroup
                  heading="Sites"
                  entries={filteredSites}
                  selectedId={selectedItemId}
                  onSelect={goTo}
                />
              </>
            )}
          </div>

          <div className="jump-palette__footer">
            <span><kbd>Up</kbd><kbd>Down</kbd> move</span>
            <span><kbd>Enter</kbd> open</span>
            <span><kbd>Esc</kbd> close</span>
            <span className="jump-palette__permission">Results respect your permissions</span>
          </div>
        </dialog>,
        document.body,
      ) : null}
    </>
  );
}

function JumpPageGroup({
  heading,
  items,
  selectedKey,
  onSelect,
}: {
  heading: string;
  items: readonly NavItem[];
  selectedKey: string | undefined;
  onSelect: (item: NavItem) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="jump-palette__group" role="group" aria-label={heading}>
      <div className="jump-palette__group-heading">{heading}</div>
      {items.map((item) => {
        const details = PAGE_DETAILS[item.key] ?? FALLBACK_DETAILS;
        return (
          <button
            key={item.key}
            id={`jump-page-${item.key}`}
            className="jump-palette__item"
            type="button"
            role="option"
            tabIndex={-1}
            aria-label={`${item.label}, ${details.description}, Page`}
            aria-selected={selectedKey === item.key}
            onClick={() => onSelect(item)}
          >
            <PageIcon />
            <span className="jump-palette__item-copy">
              <strong>{item.label}</strong>
              <span>{details.description}</span>
            </span>
            <span className="jump-palette__item-kind">Page</span>
          </button>
        );
      })}
    </div>
  );
}

function JumpObjectGroup({
  heading,
  entries,
  selectedId,
  onSelect,
}: {
  heading: string;
  entries: readonly JumpEntry[];
  selectedId: string | undefined;
  onSelect: (entry: JumpEntry) => void;
}) {
  if (entries.length === 0) return null;

  return (
    <div className="jump-palette__group" role="group" aria-label={heading}>
      <div className="jump-palette__group-heading">{heading}</div>
      {entries.map((entry) => (
        <button
          key={entry.id}
          id={entry.id}
          className="jump-palette__item"
          type="button"
          role="option"
          tabIndex={-1}
          aria-label={`${entry.label}, ${entry.description}, ${entry.kind}`}
          aria-selected={selectedId === entry.id}
          onClick={() => onSelect(entry)}
        >
          {entry.kind === "Officer" ? <OfficerIcon /> : <SiteIcon />}
          <span className="jump-palette__item-copy">
            <strong>{entry.label}</strong>
            <span>{entry.description}</span>
          </span>
          <span className="jump-palette__item-kind">{entry.kind}</span>
        </button>
      ))}
    </div>
  );
}

function SearchIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="10.5" cy="10.5" r="5.5" />
      <path d="m15 15 4.2 4.2" />
    </svg>
  );
}

function PageIcon() {
  return (
    <svg className="jump-palette__item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M7 4.5h7l3 3V19.5H7z" />
      <path d="M14 4.5v3h3M9.5 11.5h5M9.5 14.5h5" />
    </svg>
  );
}

function OfficerIcon() {
  return (
    <svg className="jump-palette__item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="8.5" r="3" />
      <path d="M6.5 18.5c1.2-2.4 3.1-3.5 5.5-3.5s4.3 1.1 5.5 3.5" />
    </svg>
  );
}

function SiteIcon() {
  return (
    <svg className="jump-palette__item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5 19.5V8.5l7-4 7 4v11" />
      <path d="M10 19.5v-5h4v5" />
    </svg>
  );
}
